import React from "react";
import clsx from "clsx";

interface Tab {
  label: string;
  content: React.ReactNode;
}

interface TabsProps {
  tabs: Tab[];
  /** Active tab index (0-based) */
  activeTab: number;
  /** Callback when tab changes */
  onTabChange: (index: number) => void;
  /** Optional content to render on the right of the tab list */
  rightContent?: React.ReactNode;
}

/**
 * Tabs component - fully controlled
 * The parent component manages the active tab state
 */
export const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onTabChange, rightContent }) => {
  return (
    <div className="flex flex-col">
      <div className="relative flex flex-row items-end border-b border-lifeguards-blue/40">
        {/* Scrollable tabs container */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex flex-row gap-2 min-w-max pr-4">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => onTabChange(index)}
                className={clsx(
                  "relative px-4 py-2 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-lifeguards-blue focus-visible:rounded-md",
                  activeTab === index
                    ? "text-lifeguards-blue font-semibold after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-lifeguards-blue after:rounded-t-md"
                    : "text-lifeguards-blue/60 hover:text-lifeguards-blue/90"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {/* Right content - sticky on the right */}
        {rightContent && (
          <div className="mb-1 mr-1 shrink-0 bg-white">
            {rightContent}
          </div>
        )}
      </div>

      <div className="mt-4">{tabs[activeTab]?.content}</div>
    </div>
  );
};
